import { toast as sonnerToast } from 'sonner';

// Toast notification utilities with consistent styling
export const toast = {
  success: (message: string, description?: string) => {
    console.log(`[TOAST:SUCCESS] ${message}${description ? ` - ${description}` : ''}`);
    sonnerToast.success(message, {
      description,
      duration: 4000,
    });
  },

  error: (message: string, description?: string) => {
    console.log(`[TOAST:ERROR] ${message}${description ? ` - ${description}` : ''}`);
    sonnerToast.error(message, {
      description,
      duration: 6000,
    });
  },

  warning: (message: string, description?: string) => {
    console.log(`[TOAST:WARNING] ${message}${description ? ` - ${description}` : ''}`);
    sonnerToast.warning(message, {
      description,
      duration: 5000,
    });
  },

  info: (message: string, description?: string) => {
    console.log(`[TOAST:INFO] ${message}${description ? ` - ${description}` : ''}`);
    sonnerToast.info(message, {
      description,
      duration: 4000,
    });
  },

  loading: (message: string, description?: string) => {
    console.log(`[TOAST:LOADING] ${message}${description ? ` - ${description}` : ''}`);
    return sonnerToast.loading(message, {
      description,
    });
  },

  // Custom toast for API responses
  apiError: (error: unknown) => {
    let message = 'Something went wrong';
    let description = 'Please try again later';

    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      // Handle API error responses
      const apiError = error as { message?: string; status?: number; statusText?: string };
      if (apiError.message) {
        message = apiError.message;
      } else if (apiError.status) {
        message = `Error ${apiError.status}`;
        description = apiError.statusText || 'Server error occurred';
      }
    }

    console.log(`[TOAST:API_ERROR] ${message}${description ? ` - ${description}` : ''}`);
    sonnerToast.error(message, {
      description,
      duration: 6000,
    });
  },

  // Promise-based toast for async operations
  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    }
  ) => {
    console.log(`[TOAST:PROMISE] Loading: ${loading}`);
    return sonnerToast.promise(promise, {
      loading,
      success,
      error,
      duration: 4000,
    });
  },

  // Dismiss all toasts
  dismiss: () => {
    sonnerToast.dismiss();
  },

  // Custom toast with action
  action: (
    message: string,
    actionLabel: string,
    actionFn: () => void,
    description?: string
  ) => {
    console.log(`[TOAST:ACTION] ${message}${description ? ` - ${description}` : ''} (Action: ${actionLabel})`);
    sonnerToast(message, {
      description,
      action: {
        label: actionLabel,
        onClick: actionFn,
      },
      duration: 8000,
    });
  },
};

// Predefined toast messages for common scenarios
export const toastMessages = {
  // Data operations
  dataLoaded: 'Data loaded successfully',
  dataLoadError: 'Failed to load data',
  dataSaved: 'Changes saved successfully',
  dataSaveError: 'Failed to save changes',
  
  // Network operations
  networkError: 'Network connection error',
  serverError: 'Server error occurred',
  requestTimeout: 'Request timed out',
  
  // User actions
  copied: 'Copied to clipboard',
  downloadStarted: 'Download started',
  downloadComplete: 'Download complete',
  downloadError: 'Download failed',
  
  // Authentication
  loginSuccess: 'Logged in successfully',
  loginError: 'Login failed',
  logoutSuccess: 'Logged out successfully',
  sessionExpired: 'Session expired, please log in again',
  
  // Workflow operations
  workflowAdded: 'Workflow added to your collection',
  workflowRemoved: 'Workflow removed from your collection',
  workflowRunning: 'Workflow is running',
  workflowComplete: 'Workflow completed successfully',
  workflowError: 'Workflow failed to complete',
  
  // Node operations
  nodeConfigSaved: 'Node configuration saved',
  nodeConfigAutoSaved: 'Node auto-saved',
  nodeConfigSaveError: 'Failed to save node configuration',
  
  // Collaboration
  userJoined: 'User joined the workflow',
  userLeft: 'User left the workflow',
  serverConnected: 'Connected to collaboration server',
  serverDisconnected: 'Lost connection to server',
  remoteNodeAdded: 'Another user added a node',
  remoteNodeRemoved: 'Another user removed a node',
  
  // Generic
  operationComplete: 'Operation completed',
  operationFailed: 'Operation failed',
  comingSoon: 'This feature is coming soon',
  featureUnavailable: 'Feature temporarily unavailable',
};